import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from 'utils/supabaseClient';
import { backendFunction } from './backendApi';
import { checkAndGrantAwards } from 'utils/awardsSystem';
import { enrichUserAccess } from './subscription';

const UserContext = createContext();

const INACTIVITY_TIMEOUT_SECONDS = 5 * 60;

let lastActiveTime = Date.now();

function resetActivityTimer() {
    lastActiveTime = Date.now();
}

if (typeof window !== 'undefined') {
    window.addEventListener("mousemove", resetActivityTimer);
    window.addEventListener("keydown", resetActivityTimer);
    window.addEventListener("touchstart", resetActivityTimer);
}

let avatarMigrationStarted = false;

function setEnrichedUser(setUserState, nextUser) {
    setUserState(nextUser ? enrichUserAccess(nextUser) : null);
}

async function loadBillingRowByAuthId(authId) {
    if (!authId) return null;

    const { data, error } = await supabase
        .from('user_billing')
        .select('*')
        .eq('auth_id', authId)
        .maybeSingle();

    if (error) {
        console.error('Error loading billing row:', error);
        return null;
    }

    return data;
}

export function UserProvider({ children }) {
    const [firstUpdate, setFirstUpdate] = useState(true);
    const [authUser, setAuthUser] = useState(null);
    const [guestMode, setGuestMode] = useState(false);
    const [user, setUser] = useState(null);
    const [loadingSession, setLoadingSession] = useState(true);
    const [loadingUserRow, setLoadingUserRow] = useState(true);
    const [dailyProgress, setDailyProgress] = useState({ lessonsDone: 0, minutesWorked: 0 });


    const reloadUser = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoadingSession(true);
            setLoadingUserRow(true);
        }

        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();

            // Handle session errors
            if (sessionError) {
                console.error('Session error:', sessionError);
                // If there's a session error, try to refresh
                const { error: refreshError } = await supabase.auth.refreshSession();
                if (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    // Clear invalid session
                    await supabase.auth.signOut();
                    setAuthUser(null);
                    setLoadingSession(false);
                    setEnrichedUser(setUser, null);
                    setLoadingUserRow(false);
                    return;
                }
                // Retry getting session after refresh
                const { data: { session: newSession } } = await supabase.auth.getSession();
                setAuthUser(newSession?.user || null);
            } else {
                setAuthUser(session?.user || null);
            }

            const rawUser = session?.user;
            setLoadingSession(false);

            if (!rawUser) {
                try {
                    const stored = localStorage.getItem('kodeo.guestUser');
                    if (stored) {
                        const guestData = JSON.parse(stored);
                        setGuestMode(true);
                        if (typeof window !== 'undefined') window.guestMode = true;
                        setEnrichedUser(setUser, { ...guestData, isGuest: true, is_admin: false });
                        setLoadingUserRow(false);
                        return;
                    }
                } catch { }
                // Ensure guest mode is properly cleared when no user
                setGuestMode(false);
                if (typeof window !== 'undefined') window.guestMode = false;
                setEnrichedUser(setUser, null);
                setLoadingUserRow(false);
                return;
            }

            // Clear guest mode when we have a real user
            setGuestMode(false);
            if (typeof window !== 'undefined') window.guestMode = false;

            const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('auth_id', rawUser.id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    await supabase.auth.signOut();
                }
                setEnrichedUser(setUser, null);
                setLoadingUserRow(false);
                console.error('Error loading user:', error);
            } else if (data) {
                const isExternalAvatar = (url) => {
                    if (!url) return true;
                    try {
                        const parsed = new URL(url);
                        return !(
                            parsed.hostname.includes('supabase.co') ||
                            parsed.hostname.includes('.supabase.in') ||
                            parsed.hostname.includes('api.dicebear.com')
                        );
                    } catch {
                        return true;
                    }
                };

                let finalAvatar = data.avatar;

                if (!finalAvatar) {
                    finalAvatar = `https://api.dicebear.com/9.x/initials/svg?seed=${data.username}&backgroundType=gradientLinear&fontFamily=Trebuchet MS&fontSize=47`;
                    await supabase.from('users').update({ avatar: finalAvatar }).eq('auth_id', rawUser.id);
                } else if (isExternalAvatar(finalAvatar) && !avatarMigrationStarted) {
                    avatarMigrationStarted = true;

                    const { data, error } = await backendFunction('avatar', {
                        method: 'POST',
                        body: {
                            userId: rawUser.id,
                            avatarUrl: finalAvatar,
                        },
                    });

                    if (error) {
                        console.warn('Avatar save failed:', error);
                    } else {
                        const { path } = data;
                        finalAvatar = path;
                        console.log('Avatar saved successfully.');
                    }
                }

                const billingData = await loadBillingRowByAuthId(rawUser.id);
                setEnrichedUser(setUser, { ...data, ...billingData, userBilling: billingData, avatar: finalAvatar });
            } else {
                console.error('Failed to load user:', error);
                setEnrichedUser(setUser, null);
            }

            setLoadingUserRow(false);
        } catch (err) {
            console.error('Error in reloadUser:', err);
            setLoadingSession(false);
            setLoadingUserRow(false);
            setEnrichedUser(setUser, null);
            setAuthUser(null);
        }
    }, []);

    const startGuest = useCallback(() => {
        const guestUser = {
            username: 'Guest',
            isGuest: true,
            is_admin: false,
            currentUnit: 1,
            currentConcept: 1,
            currentConceptProgress: 1,
        };

        setGuestMode(true);
        setEnrichedUser(setUser, guestUser);
        if (typeof window !== 'undefined') {
            window.guestMode = true;
            try {
                localStorage.setItem('kodeo.guestUser', JSON.stringify(guestUser));
            } catch { }
        }
    }, []);

    const endGuest = useCallback(() => {
        setGuestMode(false);
        if (typeof window !== 'undefined') {
            window.guestMode = false;
            localStorage.removeItem('kodeo.guestUser');
        }
        setEnrichedUser(setUser, null);
    }, []);

    useEffect(() => {
        reloadUser(true);

        const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event);

            // Handle token refresh errors
            if (event === 'TOKEN_REFRESHED') {
                console.log('Token refreshed successfully');
            } else if (event === 'SIGNED_OUT') {
                console.log('User signed out');
                setEnrichedUser(setUser, null);
                setAuthUser(null);
            } else if (event === 'USER_UPDATED') {
                console.log('User updated, reloading...');
                reloadUser(false);
            } else if (event === 'SIGNED_IN') {
                console.log('User signed in, reloading...');
                reloadUser(false);
            }

            // Handle session errors
            if (!session && event !== 'SIGNED_OUT' && authUser) {
                console.warn('Session lost, attempting to reload...');
                reloadUser(false);
            }
        });

        return () => sub?.subscription.unsubscribe();
    }, [reloadUser]);


    async function triggerAwardCheck(userId) {
        if (!userId) return;
        try {
            const updatedAwards = await checkAndGrantAwards(userId);
            if (updatedAwards) {
                setUser(prev => ({ ...prev, awards: updatedAwards }));
                console.log("New award granted!");
            }
        } catch (err) {
            console.error("Error checking awards:", err);
        }
    }

    async function incrementLessonDone(userId) {

        console.log("Incrementing lesson for user:", userId);

        const todayStr = new Date().toLocaleDateString('sv-SE');

        // Step 1: Ensure row exists (no overwrite of other fields)
        const { error: upsertError } = await supabase
            .from("user_daily_progress")
            .upsert({
                user_id: userId,
                date: todayStr
            }, { onConflict: ['user_id', 'date'] });

        if (upsertError) {
            console.error("Lesson upsert error:", upsertError);
            return;
        }

        // Step 2: Fetch current count
        const { data, error: fetchError } = await supabase
            .from("user_daily_progress")
            .select("lessons_done")
            .eq("user_id", userId)
            .eq("date", todayStr)
            .maybeSingle();

        if (fetchError) {
            console.error("Lesson fetch error:", fetchError);
            return;
        }

        const current = data?.lessons_done ?? 0;

        // Step 3: Increment only `lessons_done`
        const { error: updateError } = await supabase
            .from("user_daily_progress")
            .update({ lessons_done: current + 1 })
            .eq("user_id", userId)
            .eq("date", todayStr);

        if (updateError) {
            console.error("Lesson update error:", updateError);
        }

        console.log("Lesson incremented for user:", userId);
        // Refresh daily progress to update UI
        refreshDailyProgress();
    }

    const refreshDailyProgress = useCallback(async () => {
        if (!user?.id || user?.isGuest) return dailyProgress;
        const todayStr = new Date().toLocaleDateString('sv-SE');

        const { data, error } = await supabase
            .from("user_daily_progress")
            .select("user_id, lessons_done, minutes_worked")
            .eq("user_id", user.id)
            .eq("date", todayStr)
            .maybeSingle();

        if (error) {
            console.error("Error fetching daily progress:", error);
            setDailyProgress({ lessonsDone: 0, minutesWorked: 0 });
        } else {
            setFirstUpdate(false);
            setDailyProgress(prev => {
                const newLessons = data?.lessons_done ?? 0;
                const newMinutes = data?.minutes_worked ?? 0;

                if (prev.lessonsDone === newLessons && prev.minutesWorked === newMinutes) {
                    return prev; // avoid triggering re-renders
                }



                return {
                    lessonsDone: newLessons,
                    minutesWorked: newMinutes,
                };
            });
        }
    }, [user?.id]);

    useEffect(() => {
        refreshDailyProgress();

        const interval = setInterval(() => {
            const now = Date.now();
            const inactiveSeconds = (now - lastActiveTime) / 1000;

            if (document.visibilityState === "visible" && inactiveSeconds < INACTIVITY_TIMEOUT_SECONDS) {
                // console.log("Refreshing daily progress due to activity");
                refreshDailyProgress();
            } else {
                console.log("Skipping daily progress refresh due to inactivity");
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [refreshDailyProgress]);

    const updateUserData = async (updates) => {
        if (!user) return;

        if (!updates || typeof updates !== 'object') {
            console.error('Invalid updates provided');
            return;
        }
        updates = Object.fromEntries(
            Object.entries(updates).filter(([_, value]) => value !== undefined)
        );

        if (user.isGuest) {
            setUser(prev => {
                const updated = {
                    ...prev,
                    ...updates,
                    currentUnit: 1,
                    currentConcept: 1,
                };
                try {
                    localStorage.setItem('kodeo.guestUser', JSON.stringify(updated));
                } catch { }
                return enrichUserAccess(updated);
            });
            return;
        }

        const { data, error } = await supabase
            .from('users')
            .update({ ...updates, updated_at: new Date() })
            .eq('id', user.id)
            .select()
            .single();

        if (error) throw error;
        setEnrichedUser(setUser, { ...user, ...data, userBilling: user?.userBilling || null });
    };

    const logout = async (options = {}) => {
        if (guestMode || !user) {
            return;
        }

        const { error } = await supabase.auth.signOut(options);
        for (let key in localStorage) {
            if (key.startsWith("kodeo.concept-")) {
                localStorage.removeItem(key);
            }
        }
        localStorage.removeItem('kodeo.guestUser');
        avatarMigrationStarted = false;
        if (error) {
            console.error('Error logging out:', error);
            if (options.scope !== 'local') {
                return;
            }
        }

        setEnrichedUser(setUser, null);
        setAuthUser(null);
        console.log('User logged out successfully');
    };

    useEffect(() => {
        // console.log('User context updated:', user);
    }, [user]);

    return (
        <UserContext.Provider value={{
            user,
            authUser,
            loading: loadingSession || loadingUserRow,
            updateUserData,
            logout,
            dailyProgress,
            refreshDailyProgress,
            incrementLessonDone,
            triggerAwardCheck,
            reloadUser,
            startGuest,
            endGuest,
            guestMode,
            hasPaidAccess: user?.hasPaidAccess ?? false,
            isBillingExempt: user?.isBillingExempt ?? false,
            subscriptionStatus: user?.subscriptionStatus ?? null,
        }}>
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    return useContext(UserContext);
}

export function useDailyProgress() {
    const { dailyProgress, loading, refreshDailyProgress, incrementLessonDone } = useUser();
    return {
        loading,
        lessonsComplete: dailyProgress.lessonsDone,
        minutesComplete: dailyProgress.minutesWorked,
        refreshProgress: refreshDailyProgress,
        incrementLessonDone
    };
}

export function useUserStats() {
    const { user } = useUser();

    const logUserStat = async ({
        unitNum,
        conceptNum,
        lessonNum,
        timeSpentMs,
        isCorrect,
        generatedHelp = false
    }) => {
        if (!user?.id) {
            console.error('No user logged in, cannot log user stat');
            return;
        }

        // Validate required parameters
        if (typeof unitNum !== 'number' || typeof conceptNum !== 'number' || typeof lessonNum !== 'number') {
            console.error('Invalid unit/concept/lesson numbers provided');
            return;
        }

        if (typeof timeSpentMs !== 'number' || timeSpentMs < 0) {
            console.error('Invalid time spent provided');
            return;
        }

        if (typeof isCorrect !== 'boolean') {
            console.error('Invalid isCorrect value provided');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('user_stats')
                .insert({
                    user_id: user.id,
                    timestamp: new Date().toISOString(),
                    unit_num: unitNum,
                    concept_num: conceptNum,
                    lesson_num: lessonNum,
                    time_spent_ms: timeSpentMs,
                    is_correct: isCorrect,
                    generated_help: generatedHelp
                });

            if (error) {
                console.error('Error logging user stat:', error);
                throw error;
            }

            console.log('User stat logged successfully');
            return data;
        } catch (error) {
            console.error('Failed to log user stat:', error);
            throw error;
        }
    };

    return {
        logUserStat
    };
}

export function useUserStatsHistory() {
    const { user } = useUser();
    const [statsHistory, setStatsHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchStatsHistory = useCallback(async (maxPoints = 50) => {
        if (!user?.id) return [];

        setLoading(true);
        try {
            // First, get the earliest timestamp to determine range
            const { data: earliestData, error: earliestError } = await supabase
                .from('user_stats')
                .select('timestamp')
                .eq('user_id', user.id)
                .order('timestamp', { ascending: true })
                .limit(1);

            if (earliestError) {
                console.error('Error fetching earliest timestamp:', earliestError);
                return [];
            }

            if (!earliestData || earliestData.length === 0) {
                return [];
            }

            // Get all user stats from earliest to now
            const { data, error } = await supabase
                .from('user_stats')
                .select('timestamp, time_spent_ms, is_correct')
                .eq('user_id', user.id)
                .order('timestamp', { ascending: true });

            if (error) {
                console.error('Error fetching stats history:', error);
                return [];
            }

            // Group by day and aggregate
            const dailyStats = {};

            data?.forEach(stat => {
                const date = new Date(stat.timestamp).toLocaleDateString('sv-SE'); // YYYY-MM-DD format
                if (!dailyStats[date]) {
                    dailyStats[date] = {
                        date,
                        minutes_worked: 0,
                        lessons_done: 0,
                        total_attempts: 0,
                        correct_attempts: 0
                    };
                }

                dailyStats[date].minutes_worked += (stat.time_spent_ms || 0) / (1000 * 60); // Convert to minutes
                dailyStats[date].total_attempts++;
                if (stat.is_correct) {
                    dailyStats[date].lessons_done++;
                    dailyStats[date].correct_attempts++;
                }
            });

            // Convert to array and sort by date
            const allDays = Object.values(dailyStats).sort((a, b) => new Date(a.date) - new Date(b.date));

            // Cap outlier minutes worked values
            const maxReasonableMinutes = 120; // Cap at 2 hours per day
            allDays.forEach(day => {
                if (day.minutes_worked > maxReasonableMinutes) {
                    day.minutes_worked = maxReasonableMinutes;
                }
                // Round to 1 decimal place for cleaner display
                day.minutes_worked = Math.round(day.minutes_worked * 10) / 10;
            });

            // If we have more days than maxPoints, sample them evenly
            let result = allDays;
            if (allDays.length > maxPoints) {
                result = [];
                const step = allDays.length / maxPoints;
                for (let i = 0; i < maxPoints; i++) {
                    const index = Math.floor(i * step);
                    result.push(allDays[index]);
                }
            }

            setStatsHistory(result);
            return result;
        } catch (error) {
            console.error('Failed to fetch stats history:', error);
            return [];
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        if (user?.id) {
            fetchStatsHistory();
        }
    }, [user?.id, fetchStatsHistory]);

    return {
        statsHistory,
        loading,
        refreshStatsHistory: fetchStatsHistory
    };
}
